/**
 * Système de templates centralisé pour tâches et récompenses
 * 
 * Ce fichier contient tous les templates de base qui peuvent être :
 * - Sélectionnés par défaut pour les nouveaux utilisateurs
 * - Modifiés globalement pour tous les nouveaux utilisateurs
 * - Traduits dans différentes langues
 * - Filtrés par âge uniquement (plus de filtrage par genre)
 * 
 * NOTE: Toutes les tâches et récompenses sont neutres en genre pour promouvoir l'égalité
 * et éviter les stéréotypes. Les enfants peuvent choisir librement leurs activités.
 */

export interface BaseTemplate {
    id: string;
    emoji?: string; // @deprecated - Utiliser 'icon' à la place. Conservé pour rétrocompatibilité
    icon?: string; // Nom de l'icône Lucide (ex: 'Droplet', 'Apple', etc.)
    priority: number; // 1-10, plus élevé = plus important
    minAge?: number;
    maxAge?: number;
    gender?: 'neutral' | 'boy' | 'girl';
    category: string;
    tags: string[]; // Pour faciliter la recherche et le filtrage
    selected_for_onboarding?: boolean; // Pré-sélectionné par défaut lors de l'onboarding
    // Posés seulement sur un modèle lu en base (catalog_templates) : les textes
    // par langue remplacent alors titleKey/descriptionKey, et `points` la
    // valeur calculée par l'écran.
    titles?: Record<string, string>;
    descriptions?: Record<string, string>;
    points?: number;
}

export interface TaskTemplate extends BaseTemplate {
    type: 'task';
    titleKey: string; // Clé de traduction
    descriptionKey: string; // Clé de traduction
    estimatedDuration?: number; // En minutes
    difficulty: 'easy' | 'medium' | 'hard';
}

export interface RewardTemplate extends BaseTemplate {
    type: 'reward';
    titleKey: string; // Clé de traduction
    descriptionKey: string; // Clé de traduction
    cost?: 'free' | 'low' | 'medium' | 'high';
    frequency?: 'daily' | 'weekly' | 'monthly' | 'special';
    // Garde-fous pour certaines récompenses sensibles
    restrictions?: {
        maxDuration?: number; // Durée maximale en minutes (pour temps d'écran)
        minAge?: number; // Âge minimum requis (pour réseaux sociaux)
        maxAmount?: number; // Montant maximum en euros (pour argent)
        requiresSupervision?: boolean; // Nécessite une supervision parentale
        warningMessage?: string; // Message d'avertissement à afficher
    };
}

// === TEMPLATES DE TÂCHES ===

export const TASK_TEMPLATES: TaskTemplate[] = [
    // ======================================
    // 💪 BIEN-ÊTRE & ÉNERGIE
    // Favoriser la vitalité, la santé et les bonnes habitudes physiques
    // ======================================
    {
        id: 'wellness-water-5glasses',
        type: 'task',
        emoji: '💧',
        icon: 'Droplet',
        titleKey: 'tasks.wellness.water_5glasses.title',
        descriptionKey: 'tasks.wellness.water_5glasses.description',
        category: 'wellness',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 5,
        tags: ['daily', 'health', 'hydration'],
        selected_for_onboarding: true
    },
    {
        id: 'wellness-balanced-meal',
        type: 'task',
        emoji: '🥗',
        icon: 'Leaf',
        titleKey: 'tasks.wellness.balanced_meal.title',
        descriptionKey: 'tasks.wellness.balanced_meal.description',
        category: 'wellness',
        priority: 10,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 30,
        tags: ['daily', 'health', 'nutrition'],
        selected_for_onboarding: true
    },
    {
        id: 'wellness-fruits-vegetables',
        type: 'task',
        emoji: '🍎',
        icon: 'Apple',
        titleKey: 'tasks.wellness.fruits_vegetables.title',
        descriptionKey: 'tasks.wellness.fruits_vegetables.description',
        category: 'wellness',
        priority: 10,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 20,
        tags: ['daily', 'health', 'nutrition']
    },
    {
        id: 'wellness-breakfast',
        type: 'task',
        emoji: '🥐',
        icon: 'Circle',
        titleKey: 'tasks.wellness.breakfast.title',
        descriptionKey: 'tasks.wellness.breakfast.description',
        category: 'wellness',
        priority: 10,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'health', 'nutrition', 'morning']
    },
    {
        id: 'wellness-vitamins',
        type: 'task',
        emoji: '💊',
        icon: 'Pill',
        titleKey: 'tasks.wellness.vitamins.title',
        descriptionKey: 'tasks.wellness.vitamins.description',
        category: 'wellness',
        priority: 7,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 1,
        tags: ['daily', 'health', 'routine']
    },
    {
        id: 'wellness-walk',
        type: 'task',
        emoji: '🚶',
        icon: 'Footprints',
        titleKey: 'tasks.wellness.walk.title',
        descriptionKey: 'tasks.wellness.walk.description',
        category: 'wellness',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 20,
        tags: ['daily', 'health', 'outdoor', 'sport']
    },
    {
        id: 'wellness-no-fastfood',
        type: 'task',
        emoji: '🚫',
        icon: 'XCircle',
        titleKey: 'tasks.wellness.no_fastfood.title',
        descriptionKey: 'tasks.wellness.no_fastfood.description',
        category: 'wellness',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'health', 'nutrition']
    },
    {
        id: 'wellness-shower',
        type: 'task',
        emoji: '🚿',
        icon: 'Droplet',
        titleKey: 'tasks.wellness.shower.title',
        descriptionKey: 'tasks.wellness.shower.description',
        category: 'wellness',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'health', 'hygiene']
    },
    {
        id: 'wellness-no-fried',
        type: 'task',
        emoji: '🍟',
        icon: 'UtensilsCrossed',
        titleKey: 'tasks.wellness.no_fried.title',
        descriptionKey: 'tasks.wellness.no_fried.description',
        category: 'wellness',
        priority: 6,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'health', 'nutrition']
    },
    {
        id: 'wellness-wake-ontime',
        type: 'task',
        emoji: '⏰',
        icon: 'Clock',
        titleKey: 'tasks.wellness.wake_ontime.title',
        descriptionKey: 'tasks.wellness.wake_ontime.description',
        category: 'wellness',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'routine', 'morning']
    },
    {
        id: 'wellness-bed-20h',
        type: 'task',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'tasks.wellness.bed_20h.title',
        descriptionKey: 'tasks.wellness.bed_20h.description',
        category: 'wellness',
        priority: 8,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine']
    },
    {
        id: 'wellness-bed-21h',
        type: 'task',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'tasks.wellness.bed_21h.title',
        descriptionKey: 'tasks.wellness.bed_21h.description',
        category: 'wellness',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine']
    },
    {
        id: 'wellness-bed-22h',
        type: 'task',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'tasks.wellness.bed_22h.title',
        descriptionKey: 'tasks.wellness.bed_22h.description',
        category: 'wellness',
        priority: 7,
        minAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine']
    },
    {
        id: 'wellness-bed-23h',
        type: 'task',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'tasks.wellness.bed_23h.title',
        descriptionKey: 'tasks.wellness.bed_23h.description',
        category: 'wellness',
        priority: 7,
        minAge: 12,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine']
    },

    // ======================================
    // 🏡 MAISON & ENTRAIDE
    // Participer à la vie du foyer et apprendre la responsabilité
    // ======================================
    {
        id: 'house-dishes',
        type: 'task',
        emoji: '🍽️',
        icon: 'Utensils',
        titleKey: 'tasks.house.dishes.title',
        descriptionKey: 'tasks.house.dishes.description',
        category: 'house',
        priority: 9,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['daily', 'cleaning', 'responsibility'],
        selected_for_onboarding: true
    },
    {
        id: 'house-toys',
        type: 'task',
        emoji: '🧸',
        icon: 'ToyBrick',
        titleKey: 'tasks.house.toys.title',
        descriptionKey: 'tasks.house.toys.description',
        category: 'house',
        priority: 10,
        minAge: 3,
        maxAge: 10,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'organization', 'responsibility'],
        selected_for_onboarding: true
    },
    {
        id: 'house-bed',
        type: 'task',
        emoji: '🛏️',
        icon: 'Bed',
        titleKey: 'tasks.house.bed.title',
        descriptionKey: 'tasks.house.bed.description',
        category: 'house',
        priority: 10,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 5,
        tags: ['daily', 'organization', 'independence'],
        selected_for_onboarding: true
    },
    {
        id: 'house-table-clear',
        type: 'task',
        emoji: '🍴',
        icon: 'UtensilsCrossed',
        titleKey: 'tasks.house.table_clear.title',
        descriptionKey: 'tasks.house.table_clear.description',
        category: 'house',
        priority: 9,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'family', 'responsibility'],
        selected_for_onboarding: true
    },
    {
        id: 'house-trash',
        type: 'task',
        emoji: '🗑️',
        icon: 'Trash2',
        titleKey: 'tasks.house.trash.title',
        descriptionKey: 'tasks.house.trash.description',
        category: 'house',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 5,
        tags: ['daily', 'cleaning', 'responsibility'],
        selected_for_onboarding: true
    },
    {
        id: 'house-vacuum',
        type: 'task',
        emoji: '🧹',
        icon: 'BrushCleaning',
        titleKey: 'tasks.house.vacuum.title',
        descriptionKey: 'tasks.house.vacuum.description',
        category: 'house',
        priority: 7,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['weekly', 'cleaning', 'responsibility']
    },
    {
        id: 'house-kitchen',
        type: 'task',
        emoji: '🧽',
        icon: 'Droplet',
        titleKey: 'tasks.house.kitchen.title',
        descriptionKey: 'tasks.house.kitchen.description',
        category: 'house',
        priority: 7,
        minAge: 9,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 25,
        tags: ['daily', 'cleaning', 'responsibility']
    },
    {
        id: 'house-bathroom',
        type: 'task',
        emoji: '🚽',
        icon: 'Circle',
        titleKey: 'tasks.house.bathroom.title',
        descriptionKey: 'tasks.house.bathroom.description',
        category: 'house',
        priority: 6,
        minAge: 10,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['weekly', 'cleaning', 'responsibility']
    },
    {
        id: 'house-fold-laundry',
        type: 'task',
        emoji: '👔',
        icon: 'Shirt',
        titleKey: 'tasks.house.fold_laundry.title',
        descriptionKey: 'tasks.house.fold_laundry.description',
        category: 'house',
        priority: 7,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 15,
        tags: ['weekly', 'organization', 'responsibility']
    },
    {
        id: 'house-meal-prep',
        type: 'task',
        emoji: '🥘',
        icon: 'ChefHat',
        titleKey: 'tasks.house.meal_prep.title',
        descriptionKey: 'tasks.house.meal_prep.description',
        category: 'house',
        priority: 8,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 30,
        tags: ['daily', 'cooking', 'family']
    },
    {
        id: 'house-living-room',
        type: 'task',
        emoji: '🛋️',
        icon: 'Sofa',
        titleKey: 'tasks.house.living_room.title',
        descriptionKey: 'tasks.house.living_room.description',
        category: 'house',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'organization', 'responsibility']
    },
    {
        id: 'house-bedroom',
        type: 'task',
        emoji: '🚪',
        icon: 'DoorOpen',
        titleKey: 'tasks.house.bedroom.title',
        descriptionKey: 'tasks.house.bedroom.description',
        category: 'house',
        priority: 9,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['daily', 'organization', 'responsibility']
    },
    {
        id: 'house-help-sibling',
        type: 'task',
        emoji: '👫',
        icon: 'Users',
        titleKey: 'tasks.house.help_sibling.title',
        descriptionKey: 'tasks.house.help_sibling.description',
        category: 'house',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'family', 'helping']
    },
    {
        id: 'house-garden',
        type: 'task',
        emoji: '🌱',
        icon: 'Sprout',
        titleKey: 'tasks.house.garden.title',
        descriptionKey: 'tasks.house.garden.description',
        category: 'house',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['weekly', 'nature', 'responsibility']
    },
    {
        id: 'house-sweep-floor',
        type: 'task',
        emoji: '🧹',
        icon: 'BrushCleaning',
        titleKey: 'tasks.house.sweep_floor.title',
        descriptionKey: 'tasks.house.sweep_floor.description',
        category: 'house',
        priority: 7,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['weekly', 'cleaning', 'responsibility']
    },
    {
        id: 'house-help-parent',
        type: 'task',
        emoji: '👨‍👩‍👧',
        icon: 'Users',
        titleKey: 'tasks.house.help_parent.title',
        descriptionKey: 'tasks.house.help_parent.description',
        category: 'house',
        priority: 8,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'family', 'helping']
    },
    {
        id: 'house-organize-clothes',
        type: 'task',
        emoji: '👕',
        icon: 'Shirt',
        titleKey: 'tasks.house.organize_clothes.title',
        descriptionKey: 'tasks.house.organize_clothes.description',
        category: 'house',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['weekly', 'organization', 'responsibility']
    },
    {
        id: 'house-dust',
        type: 'task',
        emoji: '🧼',
        icon: 'Circle',
        titleKey: 'tasks.house.dust.title',
        descriptionKey: 'tasks.house.dust.description',
        category: 'house',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['weekly', 'cleaning', 'responsibility']
    },
    {
        id: 'house-organize-shoes',
        type: 'task',
        emoji: '👟',
        icon: 'Footprints',
        titleKey: 'tasks.house.organize_shoes.title',
        descriptionKey: 'tasks.house.organize_shoes.description',
        category: 'house',
        priority: 5,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['weekly', 'organization', 'responsibility']
    },
    {
        id: 'house-laundry',
        type: 'task',
        emoji: '🧺',
        icon: 'WashingMachine',
        titleKey: 'tasks.house.laundry.title',
        descriptionKey: 'tasks.house.laundry.description',
        category: 'house',
        priority: 6,
        minAge: 10,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'hard',
        estimatedDuration: 30,
        tags: ['weekly', 'cleaning', 'responsibility']
    },
    {
        id: 'house-pet-care',
        type: 'task',
        emoji: '🐕',
        icon: 'Dog',
        titleKey: 'tasks.house.pet_care.title',
        descriptionKey: 'tasks.house.pet_care.description',
        category: 'house',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 15,
        tags: ['daily', 'care', 'responsibility']
    },
    {
        id: 'house-water-plants',
        type: 'task',
        emoji: '💧',
        icon: 'Droplet',
        titleKey: 'tasks.house.water_plants.title',
        descriptionKey: 'tasks.house.water_plants.description',
        category: 'house',
        priority: 6,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 5,
        tags: ['weekly', 'nature', 'care']
    },

    // ======================================
    // 💖 ATTITUDE & VALEURS
    // Encourager les qualités humaines et le savoir-vivre
    // ======================================
    {
        id: 'values-patient',
        type: 'task',
        emoji: '⏳',
        icon: 'Hourglass',
        titleKey: 'tasks.values.patient.title',
        descriptionKey: 'tasks.values.patient.description',
        category: 'values',
        priority: 9,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'character', 'emotional']
    },
    {
        id: 'values-polite',
        type: 'task',
        emoji: '🙏',
        icon: 'Heart',
        titleKey: 'tasks.values.polite.title',
        descriptionKey: 'tasks.values.polite.description',
        category: 'values',
        priority: 10,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'character', 'social'],
        selected_for_onboarding: true
    },
    {
        id: 'values-punctual',
        type: 'task',
        emoji: '⏰',
        icon: 'Clock',
        titleKey: 'tasks.values.punctual.title',
        descriptionKey: 'tasks.values.punctual.description',
        category: 'values',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'responsibility', 'routine']
    },
    {
        id: 'values-helpful',
        type: 'task',
        emoji: '🤝',
        icon: 'Handshake',
        titleKey: 'tasks.values.helpful.title',
        descriptionKey: 'tasks.values.helpful.description',
        category: 'values',
        priority: 9,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'empathy', 'social']
    },
    {
        id: 'values-good-manners',
        type: 'task',
        emoji: '🎩',
        icon: 'HandHeart',
        titleKey: 'tasks.values.good_manners.title',
        descriptionKey: 'tasks.values.good_manners.description',
        category: 'values',
        priority: 9,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'character', 'social']
    },
    {
        id: 'values-share',
        type: 'task',
        emoji: '🤲',
        icon: 'Hand',
        titleKey: 'tasks.values.share.title',
        descriptionKey: 'tasks.values.share.description',
        category: 'values',
        priority: 9,
        minAge: 3,
        maxAge: 12,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 5,
        tags: ['daily', 'social', 'empathy']
    },
    {
        id: 'values-kind',
        type: 'task',
        emoji: '❤️',
        icon: 'Heart',
        titleKey: 'tasks.values.kind.title',
        descriptionKey: 'tasks.values.kind.description',
        category: 'values',
        priority: 10,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'character', 'empathy']
    },
    {
        id: 'values-friendly',
        type: 'task',
        emoji: '😊',
        icon: 'Smile',
        titleKey: 'tasks.values.friendly.title',
        descriptionKey: 'tasks.values.friendly.description',
        category: 'values',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 0,
        tags: ['daily', 'social', 'positive']
    },

    // ======================================
    // 📘 CURIOSITÉ & APPRENTISSAGE
    // Nourrir l'esprit et encourager la créativité
    // ======================================
    {
        id: 'learning-music-practice',
        type: 'task',
        emoji: '🎹',
        icon: 'Music',
        titleKey: 'tasks.learning.music_practice.title',
        descriptionKey: 'tasks.learning.music_practice.description',
        category: 'learning',
        priority: 7,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 30,
        tags: ['daily', 'music', 'development']
    },
    {
        id: 'learning-read-book',
        type: 'task',
        emoji: '📖',
        icon: 'BookOpen',
        titleKey: 'tasks.learning.read_book.title',
        descriptionKey: 'tasks.learning.read_book.description',
        category: 'learning',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 30,
        tags: ['daily', 'education', 'development'],
        selected_for_onboarding: true
    },
    {
        id: 'learning-school-ontime',
        type: 'task',
        emoji: '🎒',
        icon: 'Backpack',
        titleKey: 'tasks.learning.school_ontime.title',
        descriptionKey: 'tasks.learning.school_ontime.description',
        category: 'learning',
        priority: 10,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'responsibility', 'routine']
    },
    {
        id: 'learning-read-10pages',
        type: 'task',
        emoji: '📚',
        icon: 'BookOpen',
        titleKey: 'tasks.learning.read_10pages.title',
        descriptionKey: 'tasks.learning.read_10pages.description',
        category: 'learning',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'education', 'reading']
    },
    {
        id: 'learning-read-20min',
        type: 'task',
        emoji: '⏱️',
        icon: 'Timer',
        titleKey: 'tasks.learning.read_20min.title',
        descriptionKey: 'tasks.learning.read_20min.description',
        category: 'learning',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 20,
        tags: ['daily', 'education', 'reading']
    },
    {
        id: 'learning-homework',
        type: 'task',
        emoji: '📝',
        icon: 'FileText',
        titleKey: 'tasks.learning.homework.title',
        descriptionKey: 'tasks.learning.homework.description',
        category: 'learning',
        priority: 10,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'hard',
        estimatedDuration: 60,
        tags: ['daily', 'education', 'responsibility'],
        selected_for_onboarding: true
    },
    {
        id: 'learning-journal',
        type: 'task',
        emoji: '📔',
        icon: 'Notebook',
        titleKey: 'tasks.learning.journal.title',
        descriptionKey: 'tasks.learning.journal.description',
        category: 'learning',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 15,
        tags: ['daily', 'creativity', 'reflection']
    },
    {
        id: 'learning-plan-day',
        type: 'task',
        emoji: '📅',
        icon: 'Calendar',
        titleKey: 'tasks.learning.plan_day.title',
        descriptionKey: 'tasks.learning.plan_day.description',
        category: 'learning',
        priority: 7,
        minAge: 9,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 10,
        tags: ['daily', 'organization', 'planning']
    },
    {
        id: 'learning-set-goals',
        type: 'task',
        emoji: '🎯',
        icon: 'Target',
        titleKey: 'tasks.learning.set_goals.title',
        descriptionKey: 'tasks.learning.set_goals.description',
        category: 'learning',
        priority: 7,
        minAge: 10,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'hard',
        estimatedDuration: 20,
        tags: ['weekly', 'planning', 'development']
    },
    {
        id: 'learning-paint-draw',
        type: 'task',
        emoji: '🎨',
        icon: 'Palette',
        titleKey: 'tasks.learning.paint_draw.title',
        descriptionKey: 'tasks.learning.paint_draw.description',
        category: 'learning',
        priority: 7,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 30,
        tags: ['daily', 'creativity', 'art']
    },
    {
        id: 'learning-make-lists',
        type: 'task',
        emoji: '📋',
        icon: 'ClipboardList',
        titleKey: 'tasks.learning.make_lists.title',
        descriptionKey: 'tasks.learning.make_lists.description',
        category: 'learning',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'organization', 'planning']
    },

    // ======================================
    // ⚽ MOUVEMENT & SPORT
    // Canaliser l'énergie et développer la discipline par l'action
    // ======================================
    {
        id: 'sports-exercise',
        type: 'task',
        emoji: '🏃‍♂️',
        icon: 'Activity',
        titleKey: 'tasks.sports.exercise.title',
        descriptionKey: 'tasks.sports.exercise.description',
        category: 'sports',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 30,
        tags: ['daily', 'health', 'sport'],
        selected_for_onboarding: true
    },
    {
        id: 'sports-run',
        type: 'task',
        emoji: '🏃',
        icon: 'Footprints',
        titleKey: 'tasks.sports.run.title',
        descriptionKey: 'tasks.sports.run.description',
        category: 'sports',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 25,
        tags: ['daily', 'health', 'sport', 'outdoor']
    },
    {
        id: 'sports-hydrate',
        type: 'task',
        emoji: '💧',
        icon: 'Droplet',
        titleKey: 'tasks.sports.hydrate.title',
        descriptionKey: 'tasks.sports.hydrate.description',
        category: 'sports',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 2,
        tags: ['daily', 'health', 'sport']
    },
    {
        id: 'sports-football',
        type: 'task',
        emoji: '⚽',
        icon: 'Circle',
        titleKey: 'tasks.sports.football.title',
        descriptionKey: 'tasks.sports.football.description',
        category: 'sports',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 45,
        tags: ['weekly', 'sport', 'team']
    },
    {
        id: 'sports-bike',
        type: 'task',
        emoji: '🚴',
        icon: 'Bike',
        titleKey: 'tasks.sports.bike.title',
        descriptionKey: 'tasks.sports.bike.description',
        category: 'sports',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 30,
        tags: ['weekly', 'sport', 'outdoor']
    },
    {
        id: 'sports-walk',
        type: 'task',
        emoji: '🚶',
        icon: 'Footprints',
        titleKey: 'tasks.sports.walk.title',
        descriptionKey: 'tasks.sports.walk.description',
        category: 'sports',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 20,
        tags: ['daily', 'health', 'outdoor']
    },
    {
        id: 'sports-swim',
        type: 'task',
        emoji: '🏊',
        icon: 'Waves',
        titleKey: 'tasks.sports.swim.title',
        descriptionKey: 'tasks.sports.swim.description',
        category: 'sports',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 45,
        tags: ['weekly', 'sport', 'health']
    },
    {
        id: 'sports-stretch',
        type: 'task',
        emoji: '🧘',
        icon: 'Circle',
        titleKey: 'tasks.sports.stretch.title',
        descriptionKey: 'tasks.sports.stretch.description',
        category: 'sports',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'health', 'wellness']
    },

    // ======================================
    // ☀️ BON DÉPART
    // Bien démarrer la journée avec énergie et bonne humeur
    // ======================================
    {
        id: 'morning-brush-teeth',
        type: 'task',
        emoji: '🦷',
        icon: 'Circle',
        titleKey: 'tasks.morning.brush_teeth.title',
        descriptionKey: 'tasks.morning.brush_teeth.description',
        category: 'morning',
        priority: 10,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 3,
        tags: ['daily', 'health', 'routine', 'hygiene'],
        selected_for_onboarding: true
    },
    {
        id: 'morning-drink-water',
        type: 'task',
        emoji: '💧',
        icon: 'Droplet',
        titleKey: 'tasks.morning.drink_water.title',
        descriptionKey: 'tasks.morning.drink_water.description',
        category: 'morning',
        priority: 9,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 2,
        tags: ['daily', 'health', 'routine']
    },
    {
        id: 'morning-wake-early',
        type: 'task',
        emoji: '🌅',
        icon: 'Sunrise',
        titleKey: 'tasks.morning.wake_early.title',
        descriptionKey: 'tasks.morning.wake_early.description',
        category: 'morning',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'routine', 'discipline']
    },
    {
        id: 'morning-exercise',
        type: 'task',
        emoji: '🤸',
        icon: 'Circle',
        titleKey: 'tasks.morning.exercise.title',
        descriptionKey: 'tasks.morning.exercise.description',
        category: 'morning',
        priority: 7,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 15,
        tags: ['daily', 'health', 'sport']
    },
    {
        id: 'morning-healthy-breakfast',
        type: 'task',
        emoji: '🥣',
        icon: 'Circle',
        titleKey: 'tasks.morning.healthy_breakfast.title',
        descriptionKey: 'tasks.morning.healthy_breakfast.description',
        category: 'morning',
        priority: 10,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 15,
        tags: ['daily', 'health', 'nutrition']
    },

    // ======================================
    // 🌙 REPOS & DÉTENTE
    // Créer une routine apaisante avant le coucher
    // ======================================
    {
        id: 'evening-no-phone',
        type: 'task',
        emoji: '📵',
        icon: 'PhoneOff',
        titleKey: 'tasks.evening.no_phone.title',
        descriptionKey: 'tasks.evening.no_phone.description',
        category: 'evening',
        priority: 9,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'hard',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine', 'digital']
    },
    {
        id: 'evening-relaxing-bath',
        type: 'task',
        emoji: '🛁',
        icon: 'Droplet',
        titleKey: 'tasks.evening.relaxing_bath.title',
        descriptionKey: 'tasks.evening.relaxing_bath.description',
        category: 'evening',
        priority: 7,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 20,
        tags: ['daily', 'wellness', 'relaxation']
    },
    {
        id: 'evening-drink-water',
        type: 'task',
        emoji: '💧',
        icon: 'Droplet',
        titleKey: 'tasks.evening.drink_water.title',
        descriptionKey: 'tasks.evening.drink_water.description',
        category: 'evening',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 2,
        tags: ['daily', 'health', 'routine']
    },
    {
        id: 'evening-make-bed',
        type: 'task',
        emoji: '🛏️',
        icon: 'Bed',
        titleKey: 'tasks.evening.make_bed.title',
        descriptionKey: 'tasks.evening.make_bed.description',
        category: 'evening',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 5,
        tags: ['daily', 'organization', 'routine']
    },
    {
        id: 'evening-brush-teeth',
        type: 'task',
        emoji: '🦷',
        icon: 'Circle',
        titleKey: 'tasks.evening.brush_teeth.title',
        descriptionKey: 'tasks.evening.brush_teeth.description',
        category: 'evening',
        priority: 10,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 3,
        tags: ['daily', 'health', 'routine', 'hygiene'],
        selected_for_onboarding: true
    },
    {
        id: 'evening-bed-before-23h',
        type: 'task',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'tasks.evening.bed_before_23h.title',
        descriptionKey: 'tasks.evening.bed_before_23h.description',
        category: 'evening',
        priority: 8,
        minAge: 12,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'routine']
    },
    {
        id: 'evening-short-walk',
        type: 'task',
        emoji: '🚶',
        icon: 'Footprints',
        titleKey: 'tasks.evening.short_walk.title',
        descriptionKey: 'tasks.evening.short_walk.description',
        category: 'evening',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'easy',
        estimatedDuration: 10,
        tags: ['daily', 'relaxation', 'outdoor']
    },
    {
        id: 'evening-enough-sleep',
        type: 'task',
        emoji: '😴',
        icon: 'Bed',
        titleKey: 'tasks.evening.enough_sleep.title',
        descriptionKey: 'tasks.evening.enough_sleep.description',
        category: 'evening',
        priority: 10,
        minAge: 3,
        maxAge: 16,
        gender: 'neutral',
        difficulty: 'medium',
        estimatedDuration: 0,
        tags: ['daily', 'sleep', 'health']
    }
];

// === TEMPLATES DE RÉCOMPENSES ===

export const REWARD_TEMPLATES: RewardTemplate[] = [
    // === ACTIVITÉS ===
    {
        id: 'activity-screen-time',
        type: 'reward',
        emoji: '📱',
        icon: 'Phone',
        titleKey: 'rewards.activite.screen_time.title',
        descriptionKey: 'rewards.activite.screen_time.description',
        category: 'activity',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['digital', 'entertainment', 'popular'],
        selected_for_onboarding: true,
        restrictions: {
            maxDuration: 30,
            warningMessage: 'Temps d\'écran limité à 30 minutes supplémentaires par jour'
        }
    },
    {
        id: 'activity-games',
        type: 'reward',
        emoji: '🎮',
        icon: 'Gamepad',
        titleKey: 'rewards.activite.games.title',
        descriptionKey: 'rewards.activite.games.description',
        category: 'activity',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['digital', 'entertainment', 'popular'],
        selected_for_onboarding: true,
        restrictions: {
            maxDuration: 45,
            warningMessage: 'Jeux vidéo limités à 45 minutes supplémentaires par jour'
        }
    },
    {
        id: 'activity-friends',
        type: 'reward',
        emoji: '👥',
        icon: 'Users',
        titleKey: 'rewards.activite.friends.title',
        descriptionKey: 'rewards.activite.friends.description',
        category: 'activity',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['social', 'friends', 'development']
    },
    {
        id: 'activity-sports',
        type: 'reward',
        emoji: '⚽',
        icon: 'Circle',
        titleKey: 'rewards.activite.sports.title',
        descriptionKey: 'rewards.activite.sports.description',
        category: 'activity',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'weekly',
        tags: ['sport', 'health', 'development']
    },
    {
        id: 'activity-cooking',
        type: 'reward',
        emoji: '👨‍🍳',
        icon: 'Circle',
        titleKey: 'rewards.activite.cooking.title',
        descriptionKey: 'rewards.activite.cooking.description',
        category: 'activity',
        priority: 5,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'weekly',
        tags: ['cooking', 'skills', 'family']
    },

    // === OBJETS ===
    {
        id: 'object-toy',
        type: 'reward',
        emoji: '🧸',
        icon: 'ToyBrick',
        titleKey: 'rewards.objet.toy.title',
        descriptionKey: 'rewards.objet.toy.description',
        category: 'object',
        priority: 8,
        minAge: 3,
        maxAge: 10,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['toy', 'play', 'childhood']
    },
    {
        id: 'object-book',
        type: 'reward',
        emoji: '📚',
        icon: 'BookOpen',
        titleKey: 'rewards.objet.book.title',
        descriptionKey: 'rewards.objet.book.description',
        category: 'object',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['education', 'reading', 'development']
    },
    {
        id: 'object-clothes',
        type: 'reward',
        emoji: '👕',
        icon: 'Shirt',
        titleKey: 'rewards.objet.clothes.title',
        descriptionKey: 'rewards.objet.clothes.description',
        category: 'object',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['fashion', 'personal', 'style']
    },
    {
        id: 'object-electronics',
        type: 'reward',
        emoji: '🎧',
        icon: 'Headphones',
        titleKey: 'rewards.objet.electronics.title',
        descriptionKey: 'rewards.objet.electronics.description',
        category: 'object',
        priority: 5,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'high',
        frequency: 'monthly',
        tags: ['technology', 'entertainment', 'expensive']
    },
    {
        id: 'object-art-supplies',
        type: 'reward',
        emoji: '🎨',
        icon: 'Palette',
        titleKey: 'rewards.objet.art_supplies.title',
        descriptionKey: 'rewards.objet.art_supplies.description',
        category: 'object',
        priority: 6,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['creativity', 'art', 'development']
    },

    // === PRIVILÈGES ===
    {
        id: 'privilege-stay-up',
        type: 'reward',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'rewards.privilege.stay_up.title',
        descriptionKey: 'rewards.privilege.stay_up.description',
        category: 'privilege',
        priority: 9,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['bedtime', 'independence', 'popular'],
        selected_for_onboarding: true
    },
    {
        id: 'privilege-choose-meal',
        type: 'reward',
        emoji: '🍕',
        icon: 'Circle',
        titleKey: 'rewards.privilege.choose_meal.title',
        descriptionKey: 'rewards.privilege.choose_meal.description',
        category: 'privilege',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['food', 'choice', 'family'],
        selected_for_onboarding: true
    },
    {
        id: 'privilege-no-chores',
        type: 'reward',
        emoji: '😎',
        icon: 'Smile',
        titleKey: 'rewards.privilege.no_chores.title',
        descriptionKey: 'rewards.privilege.no_chores.description',
        category: 'privilege',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['chores', 'break', 'popular']
    },
    {
        id: 'privilege-choose-activity',
        type: 'reward',
        emoji: '🎯',
        icon: 'Target',
        titleKey: 'rewards.privilege.choose_activity.title',
        descriptionKey: 'rewards.privilege.choose_activity.description',
        category: 'privilege',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['choice', 'independence', 'family'],
        selected_for_onboarding: true
    },
    {
        id: 'privilege-extra-allowance',
        type: 'reward',
        emoji: '💰',
        icon: 'Coins',
        titleKey: 'rewards.privilege.extra_allowance.title',
        descriptionKey: 'rewards.privilege.extra_allowance.description',
        category: 'privilege',
        priority: 5,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['money', 'independence', 'responsibility'],
        restrictions: {
            maxAmount: 5,
            requiresSupervision: true,
            warningMessage: 'Argent de poche : maximum 5€/semaine, supervision parentale recommandée'
        }
    },
    {
        id: 'privilege-small-allowance',
        type: 'reward',
        emoji: '🪙',
        icon: 'Coins',
        titleKey: 'rewards.privilege.small_allowance.title',
        descriptionKey: 'rewards.privilege.small_allowance.description',
        category: 'privilege',
        priority: 6,
        minAge: 6,
        maxAge: 10,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['money', 'learning', 'responsibility'],
        restrictions: {
            maxAmount: 2,
            requiresSupervision: true,
            warningMessage: 'Petit argent de poche : maximum 2€/semaine pour les 6-10 ans, supervision parentale obligatoire'
        }
    },

    // === DIGITAL ===
    {
        id: 'digital-app',
        type: 'reward',
        emoji: '📱',
        icon: 'Phone',
        titleKey: 'rewards.digital.app.title',
        descriptionKey: 'rewards.digital.app.description',
        category: 'digital',
        priority: 8,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['technology', 'entertainment', 'popular']
    },
    {
        id: 'digital-game',
        type: 'reward',
        emoji: '🎮',
        icon: 'Gamepad',
        titleKey: 'rewards.digital.game.title',
        descriptionKey: 'rewards.digital.game.description',
        category: 'digital',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['gaming', 'entertainment', 'popular']
    },
    {
        id: 'digital-music',
        type: 'reward',
        emoji: '🎵',
        icon: 'Circle',
        titleKey: 'rewards.digital.music.title',
        descriptionKey: 'rewards.digital.music.description',
        category: 'digital',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['music', 'entertainment', 'popular']
    },
    {
        id: 'digital-video',
        type: 'reward',
        emoji: '📺',
        icon: 'Tv',
        titleKey: 'rewards.digital.video.title',
        descriptionKey: 'rewards.digital.video.description',
        category: 'digital',
        priority: 5,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['entertainment', 'media', 'popular']
    },
    {
        id: 'digital-social',
        type: 'reward',
        emoji: '💬',
        icon: 'MessageCircle',
        titleKey: 'rewards.digital.social.title',
        descriptionKey: 'rewards.digital.social.description',
        category: 'digital',
        priority: 4,
        minAge: 13,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['social', 'communication', 'teen'],
        restrictions: {
            minAge: 13,
            maxDuration: 30,
            requiresSupervision: true,
            warningMessage: 'Réseaux sociaux : âge minimum 13 ans, temps limité à 30 min/jour, supervision parentale recommandée'
        }
    },

    // === SORTIES ===
    {
        id: 'outing-park',
        type: 'reward',
        emoji: '🌳',
        icon: 'TreePine',
        titleKey: 'rewards.sortie.park.title',
        descriptionKey: 'rewards.sortie.park.description',
        category: 'outing',
        priority: 8,
        minAge: 3,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['outdoor', 'nature', 'family']
    },
    {
        id: 'outing-cinema',
        type: 'reward',
        emoji: '🎬',
        icon: 'Video',
        titleKey: 'rewards.sortie.cinema.title',
        descriptionKey: 'rewards.sortie.cinema.description',
        category: 'outing',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['entertainment', 'family', 'special']
    },
    {
        id: 'outing-restaurant',
        type: 'reward',
        emoji: '🍽️',
        icon: 'Utensils',
        titleKey: 'rewards.sortie.restaurant.title',
        descriptionKey: 'rewards.sortie.restaurant.description',
        category: 'outing',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['food', 'family', 'special']
    },
    {
        id: 'outing-museum',
        type: 'reward',
        emoji: '🏛️',
        icon: 'Building',
        titleKey: 'rewards.sortie.museum.title',
        descriptionKey: 'rewards.sortie.museum.description',
        category: 'outing',
        priority: 5,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['education', 'culture', 'family']
    },
    {
        id: 'outing-zoo',
        type: 'reward',
        emoji: '🦁',
        icon: 'Cat',
        titleKey: 'rewards.sortie.zoo.title',
        descriptionKey: 'rewards.sortie.zoo.description',
        category: 'outing',
        priority: 4,
        minAge: 3,
        maxAge: 12,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['animals', 'nature', 'family']
    },

    // === RÉCOMPENSES EXPÉRIENCES ===
    {
        id: 'experience-pajama-day',
        type: 'reward',
        emoji: '🛌',
        icon: 'Bed',
        titleKey: 'rewards.experience.pajama_day.title',
        descriptionKey: 'rewards.experience.pajama_day.description',
        category: 'experience',
        priority: 8,
        minAge: 3,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['comfort', 'relaxation', 'special']
    },
    {
        id: 'experience-bed-breakfast',
        type: 'reward',
        emoji: '🍳',
        icon: 'Circle',
        titleKey: 'rewards.experience.bed_breakfast.title',
        descriptionKey: 'rewards.experience.bed_breakfast.description',
        category: 'experience',
        priority: 9,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['comfort', 'special', 'family']
    },
    {
        id: 'experience-fort-building',
        type: 'reward',
        emoji: '🏰',
        icon: 'Building',
        titleKey: 'rewards.experience.fort_building.title',
        descriptionKey: 'rewards.experience.fort_building.description',
        category: 'experience',
        priority: 7,
        minAge: 4,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['creativity', 'imagination', 'play']
    },
    {
        id: 'experience-camping-indoor',
        type: 'reward',
        emoji: '⛺',
        icon: 'TreePine',
        titleKey: 'rewards.experience.camping_indoor.title',
        descriptionKey: 'rewards.experience.camping_indoor.description',
        category: 'experience',
        priority: 6,
        minAge: 5,
        maxAge: 14,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['adventure', 'imagination', 'special']
    },
    {
        id: 'experience-movie-night',
        type: 'reward',
        emoji: '🎬',
        icon: 'Video',
        titleKey: 'rewards.experience.movie_night.title',
        descriptionKey: 'rewards.experience.movie_night.description',
        category: 'experience',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['entertainment', 'family', 'special'],
        selected_for_onboarding: true
    },
    {
        id: 'activity-board-games',
        type: 'reward',
        icon: 'Puzzle',
        titleKey: 'rewards.activity.board_games.title',
        descriptionKey: 'rewards.activity.board_games.description',
        category: 'activity',
        priority: 7,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['family', 'logic', 'social']
      },
      {
        id: 'activity-reading-time',
        type: 'reward',
        icon: 'Book',
        titleKey: 'rewards.activity.reading_time.title',
        descriptionKey: 'rewards.activity.reading_time.description',
        category: 'activity',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        cost: 'free',
        frequency: 'daily',
        tags: ['reading', 'calm', 'education']
      },
      {
        id: 'activity-music-time',
        type: 'reward',
        emoji: '🎵',
        icon: 'Music',
        titleKey: 'rewards.activite.music_time.title',
        descriptionKey: 'rewards.activite.music_time.description',
        category: 'activity',
        priority: 6,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['music', 'creativity', 'relax']
      },
      {
        id: 'activity-bike-ride',
        type: 'reward',
        emoji: '🚴',
        icon: 'Bike',
        titleKey: 'rewards.activite.bike_ride.title',
        descriptionKey: 'rewards.activite.bike_ride.description',
        category: 'activity',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['sport', 'outdoor', 'health']
      },
      {
        id: 'activity-walk-choice',
        type: 'reward',
        emoji: '🚶',
        icon: 'Map',
        titleKey: 'rewards.activite.walk_choice.title',
        descriptionKey: 'rewards.activite.walk_choice.description',
        category: 'activity',
        priority: 5,
        minAge: 5,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['choice', 'outdoor', 'family']
      },
      {
        id: 'activity-drawing-time',
        type: 'reward',
        emoji: '✏️',
        icon: 'PenTool',
        titleKey: 'rewards.activite.drawing_time.title',
        descriptionKey: 'rewards.activite.drawing_time.description',
        category: 'activity',
        priority: 6,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['creativity', 'calm']
      },
      {
        id: 'activity-lego-time',
        type: 'reward',
        emoji: '🧱',
        icon: 'Blocks',
        titleKey: 'rewards.activite.lego_time.title',
        descriptionKey: 'rewards.activite.lego_time.description',
        category: 'activity',
        priority: 7,
        minAge: 4,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['construction', 'creativity']
      },
      {
        id: 'activity-garden-help',
        type: 'reward',
        emoji: '🌱',
        icon: 'Sprout',
        titleKey: 'rewards.activite.garden_help.title',
        descriptionKey: 'rewards.activite.garden_help.description',
        category: 'activity',
        priority: 5,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['nature', 'learning']
      },
      {
        id: 'activity-family-game',
        type: 'reward',
        emoji: '👨‍👩‍👧',
        icon: 'Users',
        titleKey: 'rewards.activite.family_game.title',
        descriptionKey: 'rewards.activite.family_game.description',
        category: 'activity',
        priority: 8,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['family', 'bonding']
      },
      {
        id: 'activity-dance-time',
        type: 'reward',
        emoji: '💃',
        icon: 'Activity',
        titleKey: 'rewards.activite.dance_time.title',
        descriptionKey: 'rewards.activite.dance_time.description',
        category: 'activity',
        priority: 6,
        minAge: 4,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['movement', 'fun']
      },
      {
        id: 'activity-storytelling',
        type: 'reward',
        emoji: '📖',
        icon: 'MessageSquare',
        titleKey: 'rewards.activite.storytelling.title',
        descriptionKey: 'rewards.activite.storytelling.description',
        category: 'activity',
        priority: 5,
        minAge: 5,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['imagination', 'language']
      },
      {
        id: 'activity-free-play',
        type: 'reward',
        emoji: '😊',
        icon: 'Smile',
        titleKey: 'rewards.activite.free_play.title',
        descriptionKey: 'rewards.activite.free_play.description',
        category: 'activity',
        priority: 7,
        minAge: 4,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['autonomy', 'play']
      },
      {
        id: 'privilege-choose-music',
        type: 'reward',
        emoji: '🎵',
        icon: 'Music',
        titleKey: 'rewards.privilege.choose_music.title',
        descriptionKey: 'rewards.privilege.choose_music.description',
        category: 'privilege',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['choice', 'family']
      },
      {
        id: 'privilege-dessert-choice',
        type: 'reward',
        emoji: '🍰',
        icon: 'Cake',
        titleKey: 'rewards.privilege.dessert_choice.title',
        descriptionKey: 'rewards.privilege.dessert_choice.description',
        category: 'privilege',
        priority: 7,
        minAge: 5,
        maxAge: 14,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['food', 'choice']
      },
      {
        id: 'privilege-extra-story',
        type: 'reward',
        emoji: '📚',
        icon: 'BookOpen',
        titleKey: 'rewards.privilege.extra_story.title',
        descriptionKey: 'rewards.privilege.extra_story.description',
        category: 'privilege',
        priority: 6,
        minAge: 4,
        maxAge: 10,
        gender: 'neutral',
        cost: 'free',
        frequency: 'daily',
        tags: ['bedtime', 'comfort']
      },
      {
        id: 'privilege-pyjama-evening',
        type: 'reward',
        emoji: '🛌',
        icon: 'Bed',
        titleKey: 'rewards.privilege.pyjama_evening.title',
        descriptionKey: 'rewards.privilege.pyjama_evening.description',
        category: 'privilege',
        priority: 6,
        minAge: 4,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['comfort']
      },
      {
        id: 'privilege-room-rearrange',
        type: 'reward',
        emoji: '🔄',
        icon: 'Move',
        titleKey: 'rewards.privilege.room_rearrange.title',
        descriptionKey: 'rewards.privilege.room_rearrange.description',
        category: 'privilege',
        priority: 5,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['autonomy']
      },
      {
        id: 'privilege-alarm-choice',
        type: 'reward',
        emoji: '⏰',
        icon: 'AlarmClock',
        titleKey: 'rewards.privilege.alarm_choice.title',
        descriptionKey: 'rewards.privilege.alarm_choice.description',
        category: 'privilege',
        priority: 4,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['independence']
      },
      {
        id: 'privilege-sleepover',
        type: 'reward',
        emoji: '🏠',
        icon: 'Home',
        titleKey: 'rewards.privilege.sleepover.title',
        descriptionKey: 'rewards.privilege.sleepover.description',
        category: 'privilege',
        priority: 8,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['social']
      },
      {
        id: 'privilege-invite-friend',
        type: 'reward',
        emoji: '👥',
        icon: 'UserPlus',
        titleKey: 'rewards.privilege.invite_friend.title',
        descriptionKey: 'rewards.privilege.invite_friend.description',
        category: 'privilege',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['friends']
      },
      {
        id: 'privilege-movie-choice',
        type: 'reward',
        emoji: '🎬',
        icon: 'Film',
        titleKey: 'rewards.privilege.movie_choice.title',
        descriptionKey: 'rewards.privilege.movie_choice.description',
        category: 'privilege',
        priority: 7,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['entertainment']
      },
      {
        id: 'privilege-later-bedtime-weekend',
        type: 'reward',
        emoji: '🌙',
        icon: 'Moon',
        titleKey: 'rewards.privilege.later_bedtime_weekend.title',
        descriptionKey: 'rewards.privilege.later_bedtime_weekend.description',
        category: 'privilege',
        priority: 8,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['independence']
      },
      {
        id: 'privilege-choose-dessert-night',
        type: 'reward',
        emoji: '🍦',
        icon: 'IceCream',
        titleKey: 'rewards.privilege.choose_dessert_night.title',
        descriptionKey: 'rewards.privilege.choose_dessert_night.description',
        category: 'privilege',
        priority: 6,
        minAge: 5,
        maxAge: 14,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['family']
      },
      {
        id: 'privilege-skip-bath-once',
        type: 'reward',
        emoji: '🚫',
        icon: 'DropletOff',
        titleKey: 'rewards.privilege.skip_bath_once.title',
        descriptionKey: 'rewards.privilege.skip_bath_once.description',
        category: 'privilege',
        priority: 4,
        minAge: 6,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['fun']
      },
      {
        id: 'experience-baking-together',
        type: 'reward',
        emoji: '🍪',
        icon: 'Cookie',
        titleKey: 'rewards.experience.baking_together.title',
        descriptionKey: 'rewards.experience.baking_together.description',
        category: 'experience',
        priority: 7,
        minAge: 4,
        maxAge: 14,
        gender: 'neutral',
        cost: 'low',
        frequency: 'weekly',
        tags: ['family', 'cooking']
      },
      {
        id: 'experience-picnic',
        type: 'reward',
        emoji: '🧺',
        icon: 'Sun',
        titleKey: 'rewards.experience.picnic.title',
        descriptionKey: 'rewards.experience.picnic.description',
        category: 'experience',
        priority: 7,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['outdoor', 'family']
      },
      {
        id: 'experience-treasure-hunt',
        type: 'reward',
        emoji: '🗺️',
        icon: 'Map',
        titleKey: 'rewards.experience.treasure_hunt.title',
        descriptionKey: 'rewards.experience.treasure_hunt.description',
        category: 'experience',
        priority: 8,
        minAge: 5,
        maxAge: 12,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['adventure', 'play']
      },
      {
        id: 'experience-crafts-day',
        type: 'reward',
        emoji: '✂️',
        icon: 'Scissors',
        titleKey: 'rewards.experience.crafts_day.title',
        descriptionKey: 'rewards.experience.crafts_day.description',
        category: 'experience',
        priority: 6,
        minAge: 4,
        maxAge: 14,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['creativity']
      },
      {
        id: 'experience-night-walk',
        type: 'reward',
        emoji: '🔦',
        icon: 'Flashlight',
        titleKey: 'rewards.experience.night_walk.title',
        descriptionKey: 'rewards.experience.night_walk.description',
        category: 'experience',
        priority: 6,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['adventure']
      },
      {
        id: 'experience-library-visit',
        type: 'reward',
        emoji: '📚',
        icon: 'Library',
        titleKey: 'rewards.sortie.library_visit.title',
        descriptionKey: 'rewards.sortie.library_visit.description',
        category: 'outing',
        priority: 5,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['reading', 'culture']
      },
      {
        id: 'experience-swimming',
        type: 'reward',
        emoji: '🏊',
        icon: 'Waves',
        titleKey: 'rewards.sortie.swimming.title',
        descriptionKey: 'rewards.sortie.swimming.description',
        category: 'outing',
        priority: 7,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['sport']
      },
      {
        id: 'experience-bowling',
        type: 'reward',
        emoji: '🎳',
        icon: 'CircleDot',
        titleKey: 'rewards.sortie.bowling.title',
        descriptionKey: 'rewards.sortie.bowling.description',
        category: 'outing',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['fun']
      },
      {
        id: 'experience-ice-cream-trip',
        type: 'reward',
        emoji: '🍦',
        icon: 'IceCream',
        titleKey: 'rewards.sortie.ice_cream_trip.title',
        descriptionKey: 'rewards.sortie.ice_cream_trip.description',
        category: 'outing',
        priority: 7,
        minAge: 4,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['treat']
      },
      {
        id: 'experience-train-trip',
        type: 'reward',
        emoji: '🚂',
        icon: 'Train',
        titleKey: 'rewards.sortie.train_trip.title',
        descriptionKey: 'rewards.sortie.train_trip.description',
        category: 'outing',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['exploration']
      },
      {
        id: 'experience-nature-observation',
        type: 'reward',
        emoji: '🔭',
        icon: 'Binoculars',
        titleKey: 'rewards.experience.nature_observation.title',
        descriptionKey: 'rewards.experience.nature_observation.description',
        category: 'experience',
        priority: 5,
        minAge: 5,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['nature']
      },
      {
        id: 'experience-boardgame-cafe',
        type: 'reward',
        emoji: '🎲',
        icon: 'Dice5',
        titleKey: 'rewards.sortie.boardgame_cafe.title',
        descriptionKey: 'rewards.sortie.boardgame_cafe.description',
        category: 'outing',
        priority: 6,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'medium',
        frequency: 'monthly',
        tags: ['social']
      },
      {
        id: 'experience-diy-project',
        type: 'reward',
        emoji: '🔨',
        icon: 'Hammer',
        titleKey: 'rewards.experience.diy_project.title',
        descriptionKey: 'rewards.experience.diy_project.description',
        category: 'experience',
        priority: 6,
        minAge: 7,
        maxAge: 16,
        gender: 'neutral',
        cost: 'low',
        frequency: 'monthly',
        tags: ['learning']
      },
      {
        id: 'experience-photo-walk',
        type: 'reward',
        emoji: '📷',
        icon: 'Camera',
        titleKey: 'rewards.experience.photo_walk.title',
        descriptionKey: 'rewards.experience.photo_walk.description',
        category: 'experience',
        priority: 5,
        minAge: 8,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['creativity']
      },
      {
        id: 'experience-stargazing',
        type: 'reward',
        emoji: '⭐',
        icon: 'Star',
        titleKey: 'rewards.experience.stargazing.title',
        descriptionKey: 'rewards.experience.stargazing.description',
        category: 'experience',
        priority: 6,
        minAge: 6,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['science']
      },
      {
        id: 'experience-story-recording',
        type: 'reward',
        emoji: '🎤',
        icon: 'Mic',
        titleKey: 'rewards.experience.story_recording.title',
        descriptionKey: 'rewards.experience.story_recording.description',
        category: 'experience',
        priority: 5,
        minAge: 6,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['expression']
      },
      {
        id: 'experience-family-challenge',
        type: 'reward',
        emoji: '🏁',
        icon: 'Flag',
        titleKey: 'rewards.experience.family_challenge.title',
        descriptionKey: 'rewards.experience.family_challenge.description',
        category: 'experience',
        priority: 8,
        minAge: 5,
        maxAge: 16,
        gender: 'neutral',
        cost: 'free',
        frequency: 'weekly',
        tags: ['teamwork']
      },
      {
        id: 'experience-creative-free-day',
        type: 'reward',
        emoji: '✨',
        icon: 'Sparkles',
        titleKey: 'rewards.experience.creative_free_day.title',
        descriptionKey: 'rewards.experience.creative_free_day.description',
        category: 'experience',
        priority: 7,
        minAge: 4,
        maxAge: 14,
        gender: 'neutral',
        cost: 'free',
        frequency: 'monthly',
        tags: ['autonomy']
      }
];

// === FONCTIONS UTILITAIRES ===

/**
 * Obtient les catégories de tâches avec leurs métadonnées (emoji, clé de traduction)
 * Cette fonction centralise la définition des catégories pour éviter la duplication
 */
export interface TaskCategory {
    value: string;
    icon: string;
    translationKey: string;
}

export function getTaskCategories(): TaskCategory[] {
    return [
        { value: 'wellness', icon: 'Droplet', translationKey: 'tasks.form.templatePicker.categories.wellness' },
        { value: 'house', icon: 'Home', translationKey: 'tasks.form.templatePicker.categories.house' },
        { value: 'values', icon: 'Heart', translationKey: 'tasks.form.templatePicker.categories.values' },
        { value: 'learning', icon: 'BookOpen', translationKey: 'tasks.form.templatePicker.categories.learning' },
        { value: 'sports', icon: 'Activity', translationKey: 'tasks.form.templatePicker.categories.sports' },
        { value: 'morning', icon: 'Sun', translationKey: 'tasks.form.templatePicker.categories.morning' },
        { value: 'evening', icon: 'Moon', translationKey: 'tasks.form.templatePicker.categories.evening' }
    ];
}

/**
 * Obtient les catégories de récompenses avec leurs métadonnées (emoji, clé de traduction)
 * Cette fonction centralise la définition des catégories pour éviter la duplication
 */
export interface RewardCategory {
    value: string;
    icon: string;
    translationKey: string;
}

export function getRewardCategories(): RewardCategory[] {
    return [
        { value: 'activity', icon: 'Gamepad', translationKey: 'rewards.form.templatePicker.categories.activity' },
        { value: 'object', icon: 'Gift', translationKey: 'rewards.form.templatePicker.categories.object' },
        { value: 'privilege', icon: 'Star', translationKey: 'rewards.form.templatePicker.categories.privilege' },
        { value: 'digital', icon: 'Phone', translationKey: 'rewards.form.templatePicker.categories.digital' },
        { value: 'outing', icon: 'MapPin', translationKey: 'rewards.form.templatePicker.categories.outing' },
        { value: 'experience', icon: 'Sparkles', translationKey: 'rewards.form.templatePicker.categories.experience' }
    ];
}

/**
 * Mappe un emoji vers un nom d'icône Lucide
 * Cette fonction permet de convertir les emojis des templates en icônes Lucide
 */
export function getLucideIconFromEmoji(emoji: string): string | null {
    const emojiToIconMap: Record<string, string> = {
        // Bien-être & Santé
        '💧': 'Droplet',           // Eau
        '🥗': 'Leaf',             // Salade/repas équilibré
        '🍎': 'Apple',             // Fruits
        '🥐': 'Circle',            // Petit-déjeuner
        '💊': 'Pill',              // Vitamines
        '🚶': 'Footprints',        // Marche
        '🚫': 'XCircle',           // Interdit
        '🚿': 'Droplet',           // Douche
        '🍟': 'UtensilsCrossed',   // Frites
        '⏰': 'Clock',             // Réveil/heure
        '🌙': 'Moon',              // Nuit/coucher
        '🦷': 'Circle',            // Dents
        '🌅': 'Sunrise',           // Lever
        '🥣': 'Circle',            // Petit-déjeuner
        '📵': 'PhoneOff',          // Pas de téléphone
        '🛁': 'Droplet',           // Bain
        '😴': 'Bed',               // Dormir
        '💪': 'Dumbbell',          // Bien-être/force
        
        // Maison & Entraide
        '🍽️': 'Utensils',          // Vaisselle
        '🧸': 'ToyBrick',          // Jouets
        '🛏️': 'Bed',              // Lit
        '🍴': 'UtensilsCrossed',   // Couverts
        '🗑️': 'Trash2',           // Poubelle
        '🧹': 'BrushCleaning',     // Balai
        '🧽': 'Droplet',           // Nettoyage
        '🚽': 'Circle',            // Toilettes
        '👔': 'Shirt',             // Vêtements
        '🥘': 'ChefHat',           // Cuisine
        '🛋️': 'Sofa',             // Canapé
        '🚪': 'DoorOpen',          // Porte
        '👫': 'Users',             // Aide frère/sœur
        '🌱': 'Sprout',            // Jardin
        '🧺': 'WashingMachine',    // Panier à linge
        '🐕': 'Dog',               // Animal de compagnie
        '👨‍👩‍👧': 'Users',         // Aide parent
        '👕': 'Shirt',             // Vêtements
        '🧼': 'Circle',            // Nettoyage
        '👟': 'Footprints',        // Chaussures
        
        // Valeurs & Attitude
        '⏳': 'Hourglass',         // Patience
        '🙏': 'Heart',             // Poli
        '🤝': 'Handshake',        // Aide
        '🎩': 'HandHeart',        // Bonnes manières
        '🤲': 'Hand',              // Partage
        '❤️': 'Heart',            // Gentillesse
        '😊': 'Smile',            // Amical
        
        // Apprentissage
        '🎹': 'Music',            // Musique
        '📖': 'BookOpen',         // Livre
        '🎒': 'Backpack',         // École
        '📚': 'BookOpen',         // Livres
        '⏱️': 'Timer',            // Lecture 20min
        '📝': 'FileText',         // Devoirs
        '📔': 'Notebook',         // Journal
        '📅': 'Calendar',         // Planifier
        '🎯': 'Target',           // Objectifs
        '🎨': 'Palette',          // Dessin
        '📋': 'ClipboardList',    // Listes
        '📘': 'Book',             // Apprentissage
        
        // Sport
        '🏃‍♂️': 'Activity',       // Exercice
        '🏃': 'Footprints',       // Course
        '⚽': 'Circle',            // Football
        '🚴': 'Bike',             // Vélo
        '🏊': 'Waves',            // Natation
        '🧘': 'Circle',            // Étirements
        
        // Matin
        '☀️': 'Sun',              // Soleil
        
        // Soir
        // '🌙': 'Moon' déjà défini plus haut
        
        // Autres
        '📱': 'Phone',             // Écran
        '🎮': 'Gamepad',          // Jeux
        '👥': 'Users',            // Amis
        '🎧': 'Headphones',       // Électronique
        '🍕': 'Circle',            // Choisir repas
        '😎': 'Smile',           // Pas de corvées
        '💰': 'Coins',           // Argent
        '🪙': 'Coins',           // Petit argent
        '📺': 'Tv',              // Vidéo
        '💬': 'MessageCircle',   // Social
        '🌳': 'TreePine',         // Parc
        '🎬': 'Video',            // Cinéma
        '🏛️': 'Building',        // Musée
        '🦁': 'Cat',             // Zoo
        '🛌': 'Bed',             // Pyjama
        '🏰': 'Building',        // Fort
        '⛺': 'TreePine',         // Camping
    };
    
    return emojiToIconMap[emoji] || null;
}
